<template>
  <Teleport to="body">
    <Modal v-model="show">
      <ModalContent size="xlarge">
        <template #header>
          <Heading level="h2">{{ t('networking.port_forwarding_help_title') }}</Heading>
        </template>

        <div class="tw-space-y-6">
          <Heading level="h4">{{ t('networking.port_forwarding_help_hub_title') }}</Heading>

          <Text>{{ t('networking.port_forwarding_help_intro') }}</Text>

          <svg
            viewBox="0 80 880 335"
            class="tw-w-full tw-rounded-[12px] tw-bg-white/5"
            role="img"
            :aria-label="t('networking.port_forwarding_help_title')"
          >
            <g fill="none" stroke-width="2" class="tw-stroke-white/40">
              <line x1="290" y1="140" x2="340" y2="140" />
              <line
                v-for="station in stations"
                :key="`line-${station.name}`"
                x1="440"
                y1="180"
                :x2="station.x"
                y2="280"
              />
            </g>

            <text x="315" y="130" text-anchor="middle" font-size="12" class="tw-fill-white/68">
              {{ t('networking.port_forwarding_help_lan') }}
            </text>

            <rect x="70" y="105" width="220" height="70" rx="10" fill="#34323f" class="tw-stroke-white/40" />
            <text x="180" y="135" text-anchor="middle" font-size="15" font-weight="700" class="tw-fill-white">
              {{ t('networking.port_forwarding_help_monitoring_pc') }}
            </text>
            <text x="180" y="157" text-anchor="middle" font-size="12" class="tw-fill-white/68">
              {{ t('networking.port_forwarding_help_monitoring_pc_note') }}
            </text>

            <rect x="340" y="100" width="200" height="80" rx="10" fill="#34323f" class="tw-stroke-white/40" />
            <text x="440" y="130" text-anchor="middle" font-size="16" font-weight="700" class="tw-fill-white">
              RoboCam A
            </text>
            <text x="440" y="152" text-anchor="middle" font-size="13" class="tw-fill-white/68">
              {{ t('networking.port_forwarding_help_forwarding_off') }}
            </text>
            <text x="440" y="170" text-anchor="middle" font-size="12" class="tw-fill-white/50">LAN 10.210.A.0/24</text>

            <g v-for="station in stations" :key="station.name">
              <rect
                :x="station.x - 85"
                y="280"
                width="170"
                height="80"
                rx="10"
                fill="#23393a"
                class="tw-stroke-primary"
              />
              <text :x="station.x" y="310" text-anchor="middle" font-size="16" font-weight="700" class="tw-fill-white">
                RoboCam {{ station.name }}
              </text>
              <text :x="station.x" y="332" text-anchor="middle" font-size="13" class="tw-fill-primary">
                {{ t('networking.port_forwarding_help_forwarding_on') }}
              </text>
              <text :x="station.x" y="350" text-anchor="middle" font-size="12" class="tw-fill-white/50">
                WAN 10.210.A.x
              </text>
            </g>

            <text x="440" y="395" text-anchor="middle" font-size="12" class="tw-fill-white/68">
              {{ t('networking.port_forwarding_help_wan_to_a') }}
            </text>
          </svg>

          <ol class="tw-list-decimal tw-space-y-2 tw-pl-6">
            <li v-for="step in steps" :key="step">
              <Text>{{ t(step) }}</Text>
            </li>
          </ol>

          <Text class="tw-text-white/68">{{ t('networking.port_forwarding_help_limit') }}</Text>

          <Heading level="h4" class="tw-pt-4">{{ t('networking.port_forwarding_help_vlan_title') }}</Heading>

          <Text>{{ t('networking.port_forwarding_help_vlan_intro') }}</Text>

          <svg
            viewBox="0 0 880 300"
            class="tw-w-full tw-rounded-[12px] tw-bg-white/5"
            role="img"
            :aria-label="t('networking.port_forwarding_help_vlan_title')"
          >
            <g fill="none" stroke-width="2" class="tw-stroke-white/40">
              <line x1="440" y1="85" x2="440" y2="115" />
              <line
                v-for="station in vlanStations"
                :key="`vlan-line-${station.name}`"
                :x1="station.x"
                y1="155"
                :x2="station.x"
                y2="190"
              />
            </g>

            <rect x="330" y="20" width="220" height="65" rx="10" fill="#34323f" class="tw-stroke-white/40" />
            <text x="440" y="48" text-anchor="middle" font-size="15" font-weight="700" class="tw-fill-white">
              {{ t('networking.port_forwarding_help_monitoring_pc') }}
            </text>
            <text x="440" y="70" text-anchor="middle" font-size="12" class="tw-fill-white/68">
              {{ t('networking.port_forwarding_help_vlan_pc_note') }}
            </text>

            <rect x="40" y="115" width="800" height="40" rx="10" fill="#34323f" class="tw-stroke-white/40" />
            <text x="440" y="140" text-anchor="middle" font-size="14" font-weight="700" class="tw-fill-white">
              {{ t('networking.port_forwarding_help_vlan_name') }}
            </text>

            <g v-for="station in vlanStations" :key="`vlan-${station.name}`">
              <rect
                :x="station.x - 85"
                y="190"
                width="170"
                height="80"
                rx="10"
                fill="#23393a"
                class="tw-stroke-primary"
              />
              <text :x="station.x" y="220" text-anchor="middle" font-size="16" font-weight="700" class="tw-fill-white">
                RoboCam {{ station.name }}
              </text>
              <text :x="station.x" y="242" text-anchor="middle" font-size="13" class="tw-fill-primary">
                {{ t('networking.port_forwarding_help_forwarding_on') }}
              </text>
              <text :x="station.x" y="260" text-anchor="middle" font-size="12" class="tw-fill-white/50">
                {{ t('networking.port_forwarding_help_vlan_wan') }}
              </text>
            </g>
          </svg>

          <ol class="tw-list-decimal tw-space-y-2 tw-pl-6">
            <li v-for="step in vlanSteps" :key="step">
              <Text>{{ t(step) }}</Text>
            </li>
          </ol>
        </div>

        <template #footer="{ reject }">
          <Button color="transparent" outlined @click="reject">{{ t('networking.port_forwarding_help_close') }}</Button>
        </template>
      </ModalContent>
    </Modal>
  </Teleport>
</template>

<script setup>
import { useI18n } from 'vue-i18n';

import { Button, Heading, Modal, ModalContent, Text } from '@slideslive/fuse-kit/vue';

const show = defineModel();

const { t } = useI18n();

const stations = [
  { name: 'B1', x: 130 },
  { name: 'B2', x: 335 },
  { name: 'B3', x: 545 },
  { name: 'B4', x: 750 },
];

const vlanStations = [
  { name: '1', x: 130 },
  { name: '2', x: 335 },
  { name: '3', x: 545 },
  { name: '4', x: 750 },
];

const vlanSteps = [
  'networking.port_forwarding_help_vlan_step_connect',
  'networking.port_forwarding_help_vlan_step_forwarding',
  'networking.port_forwarding_help_vlan_step_open',
];

const steps = [
  'networking.port_forwarding_help_step_a',
  'networking.port_forwarding_help_step_b',
  'networking.port_forwarding_help_step_pc',
  'networking.port_forwarding_help_step_open',
];
</script>
